import { Spinner, Table } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import type { PhoneNumber } from 'lib-shared-legacy-types';
import { FC, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import type { FlowCallActionT } from 'components/powerCallFlow/api/types';
import type { WithFlowsInjectedPropsT } from 'hocs';
import type { FlowT } from 'hocs/flows/api/types';
import { useNotifications } from 'hooks/useNotifications';

import MoveFlowModal from './modals/moveFlowModal';
import RemoveFlowModal from './modals/removeFlowModal';
import RenameFlowModal from './modals/renameFlowModal';
import styles from './numberTableFlows.module.scss';
import NumberTableFlowsDropdown from './numberTableFlowsDropdown';

type PropsT = {
  number: PhoneNumber;
  handleFlowFetch: VoidFunction;
} & WithFlowsInjectedPropsT<FlowCallActionT>;

const NumberTableFlows: FC<PropsT> = ({ number, flows, handleFlowFetch }) => {
  const [isItemRequestSent, setIsItemRequestSent] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [selectedFlow, setSelectedFlow] =
    useState<FlowT<FlowCallActionT> | null>(null);
  const navigate = useNavigate();
  const notify = useNotifications();

  const { items, getItemsAsyncStatus } = flows;

  const isLoading = getItemsAsyncStatus === 'loading';
  const isEmptyNumber =
    items.length === 0 &&
    isItemRequestSent &&
    getItemsAsyncStatus === 'succeeded';

  useEffect(() => {
    if (!isItemRequestSent) {
      handleFlowFetch();
      setIsItemRequestSent(true);
    }
  }, [items, isItemRequestSent, setIsItemRequestSent, handleFlowFetch]);

  const onRenameClose = useCallback(() => {
    setSelectedFlow(null);
    setIsRenameModalOpen(false);
  }, [setSelectedFlow, setIsRenameModalOpen]);

  const onMoveClose = useCallback(() => {
    setSelectedFlow(null);
    setIsMoveModalOpen(false);
  }, [setSelectedFlow, setIsMoveModalOpen]);

  const onRemoveClose = useCallback(() => {
    setSelectedFlow(null);
    setIsRemoveModalOpen(false);
  }, [setSelectedFlow, setIsRemoveModalOpen]);

  const redirectToFlow = (id: string) => navigate(`/settings/phone/flow/${id}`);

  const redirectToAuditLog = (id: string) =>
    navigate(`/settings/phone/flow/${id}/history`);

  const duplicateFlow = async (flow: FlowT<FlowCallActionT>) => {
    const { id, name: originalName, ...originalFlow } = flow;
    const name = `${originalName} (copy)`;

    try {
      const {
        data: { id },
      } = await flows.create({ ...originalFlow, name, active: false });

      redirectToFlow(id);
      notify({
        title: 'Flow copied',
        text: `Flow was succesfully copied as "${name}"`,
        className: 'success',
        time: 5000,
        icon: 'done',
      });
    } catch {
      notify({
        title: 'An error occurred',
        text: `An error occurred when trying to copy the "${originalName}" flow`,
        icon: 'exclamation',
        className: 'danger',
      });
    }
  };

  const renderLoadingStatus = () => {
    if (isLoading) {
      return <Spinner />;
    }

    if (isEmptyNumber) {
      return <span>There are no flows for this number</span>;
    }

    return null;
  };

  const isLoadingStatusShown = isLoading || isEmptyNumber;

  return (
    <>
      <Table headerWithBackground={false} framed={false}>
        <Table.Tbody>
          {isLoadingStatusShown && (
            <Table.Tr>
              <Table.Td className={styles.loading}>
                {renderLoadingStatus()}
              </Table.Td>
            </Table.Tr>
          )}
          {items.map((flow) => {
            const { name, id, active } = flow;

            const onEdit = () => redirectToFlow(id);
            const onAudit = () => redirectToAuditLog(id);
            const onDuplicate = () => duplicateFlow(flow);
            const onRename = () => {
              setSelectedFlow(flow);
              setIsRenameModalOpen(true);
            };
            const onMove = () => {
              setSelectedFlow(flow);
              setIsMoveModalOpen(true);
            };
            const onRemove = () => {
              setSelectedFlow(flow);
              setIsRemoveModalOpen(true);
            };

            return (
              <Table.Tr key={id}>
                <Table.Td
                  className={classnames(styles.name, {
                    [styles.nameIsActive]: active,
                  })}
                >
                  <span>{name}</span>
                </Table.Td>
                <Table.Td className={styles.dropdownWrapper}>
                  <NumberTableFlowsDropdown
                    onEdit={onEdit}
                    onRename={onRename}
                    onDuplicate={onDuplicate}
                    onAudit={onAudit}
                    onMove={onMove}
                    onRemove={onRemove}
                    isActive={active}
                  />
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      <RemoveFlowModal
        isShown={isRemoveModalOpen}
        onClose={onRemoveClose}
        selectedFlow={selectedFlow}
        flows={flows}
      />
      <RenameFlowModal
        isShown={isRenameModalOpen}
        onClose={onRenameClose}
        selectedFlow={selectedFlow}
        number={number}
        flows={flows}
      />
      <MoveFlowModal
        isShown={isMoveModalOpen}
        number={number}
        selectedFlow={selectedFlow}
        flows={flows}
        onClose={onMoveClose}
      />
    </>
  );
};

export default NumberTableFlows;
